import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { useAuthStore } from '@/store/authStore';
import { Hash, Users, Lock } from 'lucide-react';

export default function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [message, setMessage] = useState('');

  const { data: invite, isLoading, error } = useQuery({
    queryKey: ['invite', code],
    queryFn: () => channelsApi.getInviteByCode(code!),
    enabled: !!code,
  });

  const joinMutation = useMutation({
    mutationFn: () => channelsApi.redeemInvite(code!, message || undefined),
    onSuccess: (result) => {
      if (result.status === 'joined' || result.status === 'already_member') {
        navigate(`/channel/${result.channelId}`);
      }
    },
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h2 className="text-xl font-bold text-surface-50 mb-4">Приглашение в канал</h2>
          <p className="text-surface-400 text-sm mb-6">Войдите, чтобы принять приглашение</p>
          <Link to="/auth/login" className="btn-primary">Войти</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !invite) {
    const errMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Приглашение не найдено или недействительно';
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-surface-50 mb-2">Ошибка</h2>
          <p className="text-surface-400 text-sm mb-6">{errMsg}</p>
          <Link to="/" className="btn-primary">На главную</Link>
        </div>
      </div>
    );
  }

  const ch = invite.channel;
  const isPrivate = ch.type === 'PRIVATE';

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-brand-900/40 flex items-center justify-center mx-auto mb-4">
            {isPrivate ? <Lock size={28} className="text-brand-400" /> : <Hash size={28} className="text-brand-400" />}
          </div>

          <h2 className="text-xl font-bold text-surface-50 mb-1">{ch.displayName}</h2>
          <p className="text-surface-500 text-sm font-mono mb-2">#{ch.name}</p>

          {ch.description && (
            <p className="text-surface-400 text-sm mb-3">{ch.description}</p>
          )}

          <div className="flex items-center justify-center gap-1 text-surface-500 text-xs mb-6">
            <Users size={13} />
            <span>{ch._count.members} участников</span>
          </div>

          {joinMutation.data?.status === 'pending' && (
            <div className="bg-yellow-900/30 border border-yellow-700 text-yellow-300 text-sm rounded-lg px-3 py-3 mb-4">
              Заявка отправлена! Ожидайте одобрения администратора канала.
            </div>
          )}

          {joinMutation.error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2 mb-4">
              {(joinMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Ошибка'}
            </div>
          )}

          {!joinMutation.data && (
            <>
              {isPrivate && (
                <div className="mb-4">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Сообщение для администратора (необязательно)"
                    className="irc-input resize-none"
                    rows={2}
                    maxLength={500}
                  />
                </div>
              )}

              <button
                onClick={() => joinMutation.mutate()}
                disabled={joinMutation.isPending}
                className="btn-primary w-full"
              >
                {joinMutation.isPending
                  ? 'Отправка...'
                  : isPrivate
                    ? 'Отправить заявку'
                    : 'Вступить в канал'}
              </button>
            </>
          )}

          {joinMutation.data?.status === 'pending' && (
            <Link to="/" className="btn-ghost w-full mt-3 inline-flex">На главную</Link>
          )}
        </div>
      </div>
    </div>
  );
}
